import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import OptImage from "@/components/OptImage";

/**
 * HeroSlider — full-width hero with autoplay, prev/next, dots, swipe.
 *
 * Layout:
 *   - Mobile: aspect-ratio 4/5 (portrait — fills mobile viewport without forcing 88vh)
 *   - Desktop (≥768px): aspect-ratio 16/7 (cinematic)
 *
 * Visual treatment:
 *   - Solid #0A0A0A backdrop so it never flashes white
 *   - 700ms cross-fade between slides
 *   - Subtle ken-burns zoom on the active slide
 *   - Slim progress bar synced to autoplay
 *
 * Props:
 *  - slides : array of { id, image_url, image_url_mobile?, title?, subtitle?, button_text?,
 *                        link_type, link_target, overlay_opacity?, text_align?, text_color? }
 *  - interval (ms) : autoplay delay, default 6000
 */
export default function HeroSlider({ slides = [], interval = 6000 }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progressKey, setProgressKey] = useState(0); // resets progress bar on idx change
  const timerRef = useRef(null);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  const n = slides.length;
  const goto = (i) => {
    if (n === 0) return;
    const next = ((i % n) + n) % n;
    setIdx(next);
    setProgressKey((k) => k + 1);
  };
  const next = () => goto(idx + 1);
  const prev = () => goto(idx - 1);

  // Autoplay
  useEffect(() => {
    if (n <= 1 || paused) return;
    timerRef.current = setTimeout(() => next(), interval);
    return () => timerRef.current && clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, paused, n, interval]);

  // Keyboard
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, n]);

  if (n === 0) return null;

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    setPaused(true);
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = (e.changedTouches[0].clientX || 0) - touchStartX.current;
    const dy = (e.changedTouches[0].clientY || 0) - touchStartY.current;
    touchStartX.current = touchStartY.current = null;
    setPaused(false);
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) next(); else prev();
    }
  };

  return (
    <section
      data-testid="hero-slider"
      className="relative w-full overflow-hidden bg-[#0A0A0A] aspect-[4/5] md:aspect-[16/7]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      aria-roledescription="carousel"
    >
      {slides.map((s, i) => (
        <Slide key={s.id || i} slide={s} active={i === idx} eager={i === 0} priority={i === 0} />
      ))}

      {/* Prev / Next — desktop only */}
      {n > 1 && (
        <>
          <button
            data-testid="hero-prev-btn"
            onClick={prev}
            aria-label="Previous"
            className="hidden md:flex items-center justify-center absolute left-5 top-1/2 -translate-y-1/2 h-12 w-12 bg-white/15 hover:bg-white/95 hover:text-[#0A0A0A] text-white backdrop-blur-md border border-white/20 transition-all duration-300 z-20"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            data-testid="hero-next-btn"
            onClick={next}
            aria-label="Next"
            className="hidden md:flex items-center justify-center absolute right-5 top-1/2 -translate-y-1/2 h-12 w-12 bg-white/15 hover:bg-white/95 hover:text-[#0A0A0A] text-white backdrop-blur-md border border-white/20 transition-all duration-300 z-20"
          >
            <ChevronRight size={20} />
          </button>
        </>
      )}

      {/* Dots */}
      {n > 1 && (
        <div data-testid="hero-pagination" className="absolute bottom-6 md:bottom-8 left-0 right-0 flex items-center justify-center gap-2 z-20">
          {slides.map((_, i) => (
            <button
              key={i}
              data-testid={`hero-dot-${i}`}
              onClick={() => goto(i)}
              aria-label={`Slide ${i + 1}`}
              aria-current={i === idx}
              className={"h-1.5 transition-all duration-300 " + (i === idx ? "w-10 bg-white" : "w-5 bg-white/40 hover:bg-white/70")}
            />
          ))}
        </div>
      )}

      {/* Autoplay progress bar */}
      {n > 1 && (
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-white/10 z-20 overflow-hidden">
          <div
            key={progressKey}
            className="h-full bg-[#C8A96A]"
            style={{
              animation: paused ? "none" : `hero-progress ${interval}ms linear forwards`,
            }}
          />
        </div>
      )}

      <style>{`
        @keyframes hero-progress {
          from { width: 0%; }
          to { width: 100%; }
        }
        @keyframes hero-kenburns {
          from { transform: scale(1.0); }
          to   { transform: scale(1.08); }
        }
        .hero-slide-active .hero-kenburns {
          animation: hero-kenburns ${interval + 1500}ms ease-out forwards;
        }
      `}</style>
    </section>
  );
}

function buildHref(s) {
  if (!s) return null;
  if (s.link_type === "category" && s.link_target) return `/products?category=${encodeURIComponent(s.link_target)}`;
  if (s.link_type === "product" && s.link_target) return `/products/${s.link_target}`;
  if (s.link_type === "custom" && s.link_target) return s.link_target;
  return null;
}

function Slide({ slide, active, eager, priority }) {
  const href = buildHref(slide);
  const align = slide.text_align || "left";
  const textColor = slide.text_color || "#FFFFFF";
  const overlay = Number.isFinite(+slide.overlay_opacity) ? +slide.overlay_opacity : 0.4;
  const wrapperPos =
    align === "center" ? "items-center justify-center text-center"
    : align === "right" ? "items-center justify-end text-right"
    : "items-end md:items-center justify-start text-left";

  const isClickable = !!href;
  const isExternal = isClickable && href.startsWith("http");

  // Slide background (image + ken-burns + gradient). Pointer-events handled by the wrapping link.
  const slideMedia = (
    <>
      <div className="hero-kenburns absolute inset-0 will-change-transform">
        <OptImage
          src={slide.image_url}
          srcMobile={slide.image_url_mobile || undefined}
          alt={slide.title || "Hero"}
          sizes="100vw"
          eager={eager}
          hero={priority}
          placeholderClassName="bg-[#0A0A0A]"
          wrapperClassName="absolute inset-0 block"
          className="w-full h-full object-cover"
        />
      </div>
      {/* Gradient overlay — darker at the bottom for legibility on mobile portrait */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: `linear-gradient(180deg, rgba(0,0,0,${Math.max(0, overlay - 0.2)}) 0%, rgba(0,0,0,${Math.max(0, overlay - 0.1)}) 40%, rgba(0,0,0,${Math.min(0.85, overlay + 0.25)}) 100%)` }} />
      <div className={"relative z-10 h-full flex px-6 md:px-12 lg:px-20 pb-20 md:pb-0 " + wrapperPos}>
        <div className="max-w-xl" style={{ color: textColor }}>
          {slide.title && (
            <h1 data-testid="hero-slide-title" className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[1.0] tracking-tight text-shadow-luxury">
              {slide.title}
            </h1>
          )}
          {slide.subtitle && (
            <p className="mt-4 md:mt-6 text-sm md:text-base leading-relaxed opacity-90 max-w-md">
              {slide.subtitle}
            </p>
          )}
          {slide.button_text && (
            isClickable ? (
              // CTA is rendered as a styled span because the entire slide is already
              // the link — nesting an <a>/<Link> inside the outer link is invalid HTML.
              <span data-testid="hero-slide-cta" aria-hidden="true"
                className="mt-6 md:mt-8 inline-flex items-center gap-3 bg-[#C8A96A] group-hover:bg-[#A6864A] text-white text-xs uppercase tracking-[0.25em] px-8 md:px-10 py-3.5 md:py-4 transition-colors">
                {slide.button_text}
              </span>
            ) : (
              <span data-testid="hero-slide-cta"
                className="mt-6 md:mt-8 inline-flex items-center gap-3 bg-[#C8A96A] text-white text-xs uppercase tracking-[0.25em] px-8 md:px-10 py-3.5 md:py-4">
                {slide.button_text}
              </span>
            )
          )}
        </div>
      </div>
    </>
  );

  const baseClass = "group absolute inset-0 transition-opacity duration-700 ease-in-out bg-[#0A0A0A] " +
    (active ? "opacity-100 z-10 hero-slide-active" : "opacity-0 z-0 pointer-events-none");
  const interactiveClass = isClickable ? " cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C8A96A] focus-visible:ring-offset-0" : "";
  const commonProps = {
    "data-testid": `hero-slide-${slide.id || ""}`,
    "aria-hidden": !active,
    "aria-label": slide.title || undefined,
    className: baseClass + interactiveClass,
    // When inactive, prevent focus + interaction even if pointer-events leaks
    tabIndex: active && isClickable ? 0 : -1,
    onKeyDown: isClickable
      ? (e) => {
          // Anchors natively activate on Enter — also accept Space for parity.
          if (e.key === " " || e.code === "Space") {
            e.preventDefault();
            e.currentTarget.click();
          }
        }
      : undefined,
  };

  if (!isClickable) {
    return <div {...commonProps}>{slideMedia}</div>;
  }
  if (isExternal) {
    return (
      <a {...commonProps} href={href} target="_blank" rel="noopener noreferrer">
        {slideMedia}
      </a>
    );
  }
  return (
    <Link {...commonProps} to={href}>
      {slideMedia}
    </Link>
  );
}
